// src/utils/agent.ts
/**
 * .tuMetrics {"tu1": 0.84, "tu2": 0.86, "tu3": 3, "notes": "Parses agent proposed-action blocks from message text", "tuVersion": "2.5"}
 * .help "Extracts structured agent proposed actions from chat/message text for UI action chips."
 */
import useUserStore from '@/store/userStore';

export type ProposedAction = { action: string; data?: any };

export function extractProposedActionsFromText(text: string): {
  cleanText: string;
  actions: ProposedAction[];
} {
  if (typeof text !== "string" || !text.trim()) {
    return { cleanText: text || "", actions: [] };
  }

  let working = text;
  const actions: ProposedAction[] = [];

  // Case A: fenced block ```json ... ```
  const fenced = working.match(/```json\s*([\s\S]*?)\s*```/);
  if (fenced) {
    try {
      const payload = JSON.parse(fenced[1]);
      if (payload && payload.action) {
        // Resolve UUIDs in the action data
        const resolvedPayload = resolveUuidsInAction(payload);
        actions.push({ action: resolvedPayload.action, data: resolvedPayload.data });
      }
      working = working.replace(fenced[0], "").trim();
    } catch {
      // ignore parse error, fall through to unfenced
    }
  }

  // Case B: unfenced, single-line or multi-line JSON blob that starts with {"action":
  // Grab the last {...} in the message to avoid eating normal prose.
  if (!actions.length) {
    const curlyStart = working.lastIndexOf('{"action"');
    if (curlyStart !== -1) {
      const candidate = working.slice(curlyStart).trim();
      // Try to find a matching closing brace by walking balance
      try {
        const parsed = tryParseLooseJson(candidate);
        if (parsed && parsed.action) {
          // Resolve UUIDs in the action data
          const resolvedPayload = resolveUuidsInAction(parsed);
          actions.push({ action: resolvedPayload.action, data: resolvedPayload.data });
          working = working.slice(0, curlyStart).trim();
        }
      } catch {
        // ignore
      }
    }
  }

  return { cleanText: working, actions };
}

// Resolve friendly names to UUIDs in action data
function resolveUuidsInAction(payload: any): any {
  if (!payload || !payload.data) return payload;
  
  const allUsers = useUserStore.getState().allUsers;
  
  // Resolve assignee_id if it's a friendly name
  if (payload.data.assignee_id && typeof payload.data.assignee_id === 'string') {
    const assigneeId = payload.data.assignee_id;
    
    // Check if it's already a UUID (basic check)
    const isUuid = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(assigneeId);
    
    if (!isUuid) {
      // Try to resolve friendly name to UUID
      // Suspected bug: User carries `casualName`; the snake_case read is always
      // undefined, so resolution only ever matches on full name. Preserved.
      const foundUser = allUsers.find(u => 
        u.name?.toLowerCase() === assigneeId.toLowerCase() || 
        (u as typeof u & { casual_name?: string }).casual_name?.toLowerCase() === assigneeId.toLowerCase()
      );
      
      if (foundUser) {
        payload.data.assignee_id = foundUser.id;
      }
    }
  }
  return payload;
}

// Minimal loose JSON parser: trims trailing code-block markers or stray backticks
function tryParseLooseJson(s: string): any {
  // Strip trailing backticks or stray fences if present
  const cleaned = s
    .replace(/`+$/g, "")
    .replace(/```+$/g, "")
    .trim();
  // Also strip trailing prose after a well-formed JSON object if any
  const end = findMatchingBraceEnd(cleaned);
  const slice = end > 0 ? cleaned.slice(0, end) : cleaned;
  return JSON.parse(slice);
}

// Finds the position just after the closing brace of the first top-level JSON object
function findMatchingBraceEnd(s: string): number {
  let depth = 0;
  let inStr = false;
  let esc = false;
  for (let i = 0; i < s.length; i++) {
    const ch = s[i];
    if (inStr) {
      if (esc) { esc = false; continue; }
      if (ch === "\\") { esc = true; continue; }
      if (ch === '"') inStr = false;
      continue;
    }
    if (ch === '"') { inStr = true; continue; }
    if (ch === "{") depth++;
    else if (ch === "}") {
      depth--;
      if (depth === 0) return i + 1;
    }
  }
  return -1;
}